import { Component } from '@theme/component';

/**
 * Shows connected products as package cards and points add to cart at the selected product.
 *
 * @extends {Component}
 */
class ProductConnector extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.addEventListener('change', this.#onChange);

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener('variant:update', this.#onChange, { signal: this.#abortController.signal });

    this.#syncSelectedProduct();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#abortController?.abort();
  }

  /** @param {Event} _event */
  #onChange = (_event) => {
    this.#syncSelectedProduct();
  };

  /**
   * Points a cart form at the selected connected variant.
   * @param {HTMLFormElement} form
   * @param {string} variantId
   * @param {string} sellingPlanId
   */
  #syncCartForm(form, variantId, sellingPlanId) {
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }

    this.#setSellingPlan(form, sellingPlanId);
  }

  /**
   * Keeps a single selling plan input, or removes it for a one-time purchase.
   * @param {HTMLFormElement} form
   * @param {string} sellingPlanId
   */
  #setSellingPlan(form, sellingPlanId) {
    const sellingPlanInputs = Array.from(form.querySelectorAll('input[name="selling_plan"]'));
    if (!sellingPlanId) {
      sellingPlanInputs.forEach((input) => input.remove());
      return;
    }

    const [sellingPlanInput, ...extraSellingPlanInputs] = sellingPlanInputs;
    extraSellingPlanInputs.forEach((input) => input.remove());

    if (sellingPlanInput instanceof HTMLInputElement) {
      sellingPlanInput.value = sellingPlanId;
      return;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = sellingPlanId;
    form.append(input);
  }

  /**
   * Points instant checkout at the selected product.
   * Choice mode shows both purchase buttons when this block allows it and the product can be bought either way.
   * @param {HTMLElement} checkout
   * @param {string} variantId
   * @param {string} sellingPlanId
   * @param {boolean} requiresSellingPlan
   * @param {boolean} available
   */
  #syncInstantCheckout(checkout, variantId, sellingPlanId, requiresSellingPlan, available) {
    const showChoice = checkout.dataset.showChoiceModal === 'true';
    const offersChoice = showChoice && sellingPlanId !== '' && !requiresSellingPlan;
    checkout.dataset.purchaseMode = offersChoice ? 'choice' : 'direct';

    checkout.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;

      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement) variantInput.value = variantId;

      const includePlan =
        form.dataset.checkout === 'subscription' ||
        (form.dataset.checkout === 'direct' && !offersChoice && sellingPlanId !== '');
      this.#setSellingPlan(form, includePlan ? sellingPlanId : '');
    });

    checkout.querySelectorAll('button[type="submit"]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = !available;
    });
  }

  #syncSelectedProduct() {
    const selected = this.querySelector('input[type="radio"]:checked');
    if (!(selected instanceof HTMLInputElement)) return;

    const variantId = selected.dataset.variantId;
    if (!variantId) return;

    const details = this.closest('.product-details') ?? this.closest('.shopify-section') ?? this.ownerDocument;
    const available = selected.dataset.available === 'true';
    const sellingPlanId = selected.dataset.sellingPlanId ?? '';
    const requiresSellingPlan = selected.dataset.requiresSellingPlan === 'true';

    details.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (!(form instanceof HTMLFormElement) || form.closest('instant-checkout')) return;
      this.#syncCartForm(form, variantId, sellingPlanId);
    });

    details.querySelectorAll('instant-checkout').forEach((checkout) => {
      if (!(checkout instanceof HTMLElement)) return;
      this.#syncInstantCheckout(checkout, variantId, sellingPlanId, requiresSellingPlan, available);
    });

    details.querySelectorAll('add-to-cart-component').forEach((addToCart) => {
      if (
        !(addToCart instanceof HTMLElement) ||
        typeof addToCart.enable !== 'function' ||
        typeof addToCart.disable !== 'function'
      ) {
        return;
      }

      if (available) addToCart.enable();
      else addToCart.disable();
    });

    const section = this.closest('.shopify-section') ?? this.ownerDocument;

    const priceSource = this.querySelector(`template[data-variant-id="${variantId}"]`)?.content.querySelector(
      '[ref="priceContainer"]'
    );
    if (priceSource) {
      section.querySelectorAll('product-price [ref="priceContainer"]').forEach((current) => {
        current.replaceWith(priceSource.cloneNode(true));
      });
    }

    const sticky = section.querySelector('sticky-add-to-cart');
    if (sticky instanceof HTMLElement) {
      sticky.dataset.currentVariantId = variantId;
      sticky.dataset.variantAvailable = available ? 'true' : 'false';
      const stickyVariant = sticky.querySelector('.sticky-add-to-cart__variant');
      if (stickyVariant) stickyVariant.textContent = selected.dataset.productTitle ?? '';
    }
  }
}

if (!customElements.get('product-connector')) {
  customElements.define('product-connector', ProductConnector);
}
